import React, { useState, useEffect } from 'react';
import { useGame } from '../contexts/GameContext';
import { useDebugConfig } from '../../hooks/useDebugConfig';
import { uiActionBridge } from '../framework/uiActionBridge';
import { PlayerToolsTab } from './DevTools/PlayerToolsTab';
import { PhysicsToolsTab } from './DevTools/PhysicsToolsTab';
import { ClassToolsTab } from './DevTools/ClassToolsTab';
import { NPCToolsTab } from './DevTools/NPCToolsTab';
import { ProjectileToolsTab } from './DevTools/ProjectileToolsTab';
import { ConnectionsToolsTab } from './DevTools/ConnectionsToolsTab';
import { isTabEmpty, getWhitelist } from './DevTools/DevTools.config';
import { FeatureManagerTab } from './DevTools/FeatureManagerTab';
import { DevToolsHeader, type DevToolsTab } from './DevTools/DevToolsHeader';
import { EmptyTabState } from './DevTools/EmptyTabState';
import { WhitelistBanner } from './DevTools/WhitelistBanner';
import { contentStyle } from './DevTools/devToolsPanelStyles';
import { gameLogger as logger } from '../../../utils/GameLogger';

/**
 * DevToolsContent — propless body of the Dev Tools suite (Phase 2 fold).
 *
 * WindowHost mounts it inside the shared ToolWindow chrome (only while the
 * `devTools` window is open — opened via windowBridge.toggle('devTools') / F2).
 * The legacy CUSTOM drag/resize chrome + position/size state + onWidthChange +
 * the DevToolsHeader collapse button are all DROPPED — ToolWindow owns
 * drag/resize/minimize. Data comes from useGame() (player/otherPlayers/
 * isConnected, live since the keystone) + useDebugConfig() (showDevActionLogs);
 * dev actions dispatch via uiActionBridge → ActionDispatcher (the same path the
 * old onAction prop used).
 */
export const DevToolsContent: React.FC = () => {
  const { player, otherPlayers, isConnected } = useGame();
  const { showDevActionLogs } = useDebugConfig();
  const [activeTab, setActiveTab] = useState<DevToolsTab>('player');
  const [logMode, setLogMode] = useState(1); // Default to MINIMAL (1) to match GameLogger default

  // Log when tab changes and on initial mount
  useEffect(() => {
    logger.info('devtools', `Tab switched to: ${activeTab}`, {
      tab: activeTab,
      isEmpty: isTabEmpty(activeTab),
      whitelist: getWhitelist(activeTab),
    });
  }, [activeTab]);

  // Log initial mount
  useEffect(() => {
    logger.info('devtools', 'DevTools Panel mounted', {
      availableTabs: ['player', 'physics', 'classes', 'npcs', 'projectiles', 'connections'],
      initialTab: activeTab,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!player) return null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header / Tabs — chrome handlers (drag/collapse) omitted: ToolWindow owns them. */}
      <DevToolsHeader
        activeTab={activeTab}
        showDevActionLogs={showDevActionLogs ?? false}
        logMode={logMode}
        onTabChange={setActiveTab}
        onToggleDevActionLogs={() => {
          logger.debug('ui', `[DevTools] Dispatching action: toggle_dev_action_logs`);
          uiActionBridge.dispatch('toggle_dev_action_logs');
        }}
        onToggleLogger={() => {
          const nextMode = (logMode + 1) > 4 ? 0 : logMode + 1; // Cycle 0-4
          setLogMode(nextMode);
          uiActionBridge.dispatch('toggle_logger');
        }}
      />

      {/* Content Area */}
      <div style={contentStyle}>
        {/* Debug Mode Indicator */}
        {isTabEmpty(activeTab) && <WhitelistBanner tabId={activeTab} />}

        {activeTab === 'player' && (
          isTabEmpty('player') && getWhitelist('player').length === 0
            ? <EmptyTabState tabName="Player" tabId="player" />
            : <PlayerToolsTab player={player} onAction={(a, d) => { logger.debug('ui', `[DevTools:Player] action: ${a}`, d); uiActionBridge.dispatch(a, d); }} />
        )}
        {activeTab === 'physics' && (
          isTabEmpty('physics') && getWhitelist('physics').length === 0
            ? <EmptyTabState tabName="Physics" tabId="physics" />
            : <PhysicsToolsTab player={player} onAction={(a, d) => { logger.debug('ui', `[DevTools:Physics] action: ${a}`, d); uiActionBridge.dispatch(a, d); }} />
        )}
        {activeTab === 'classes' && (
          isTabEmpty('classes') && getWhitelist('classes').length === 0
            ? <EmptyTabState tabName="Classes" tabId="classes" />
            : <ClassToolsTab player={player} onAction={(a, d) => { logger.debug('ui', `[DevTools:Classes] action: ${a}`, d); uiActionBridge.dispatch(a, d); }} />
        )}
        {activeTab === 'npcs' && (
          isTabEmpty('npcs') && getWhitelist('npcs').length === 0
            ? <EmptyTabState tabName="NPCs" tabId="npcs" />
            : <NPCToolsTab onAction={(a, d) => { logger.debug('ui', `[DevTools:NPCs] action: ${a}`, d); uiActionBridge.dispatch(a, d); }} />
        )}
        {activeTab === 'projectiles' && (
          isTabEmpty('projectiles') && getWhitelist('projectiles').length === 0
            ? <EmptyTabState tabName="Projectiles" tabId="projectiles" />
            : <ProjectileToolsTab onAction={(a, d) => { logger.debug('ui', `[DevTools:Projectiles] action: ${a}`, d); uiActionBridge.dispatch(a, d); }} />
        )}
        {activeTab === 'connections' && (
          isTabEmpty('connections') && getWhitelist('connections').length === 0
            ? <EmptyTabState tabName="Connections" tabId="connections" />
            : <ConnectionsToolsTab isConnected={isConnected} otherPlayers={otherPlayers} player={player} onAction={(a, d) => { logger.debug('ui', `[DevTools:Network] action: ${a}`, d); uiActionBridge.dispatch(a, d); }} />
        )}
        {activeTab === 'featureManager' && (
          <FeatureManagerTab domain="devtoolsPlayer" />
        )}
      </div>
    </div>
  );
};
